'use client';

import { Suspense, useEffect, useState } from 'react';

import { createClient } from '@/common/utils/supabase/client';
import { config } from '../config';
import { getConsentCookie } from '../lib/consentCookies';
import { grantGtagConsentForEverything, setGtagUserId } from '../lib/gtag';
import { CookieBanner } from './CookieBanner';
import { GoogleTagManagerScripts } from './GoogleTagManagerScripts';

type GoogleTagManagerProps = {
  initialUserId?: string;
};

export const GoogleTagManager = ({ initialUserId }: GoogleTagManagerProps) => {
  const [isGtagLoaded, setIsGtagLoaded] = useState(false);
  const [hasSetConsent, setHasSetConsent] = useState(false);

  useEffect(() => {
    if (isGtagLoaded && !hasSetConsent) {
      const consent = getConsentCookie();

      if (consent != null) {
        grantGtagConsentForEverything();
      }

      setHasSetConsent(true);
    }
  }, [isGtagLoaded, hasSetConsent]);

  useEffect(() => {
    if (!isGtagLoaded) {
      return;
    }

    const subscription = monitorUserIdChange();
    return () => {
      subscription.unsubscribe();
    };

    function monitorUserIdChange() {
      const supabase = createClient();
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_, session) => {
        setGtagUserId(session?.user.id);
      });

      return subscription;
    }
  }, [isGtagLoaded]);

  return (
    <>
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${config.gtmId}`}
          height="0"
          width="0"
          style={{
            display: 'none',
            visibility: 'hidden',
          }}></iframe>
      </noscript>
      {/* We need to use Suspense in order to access useSearchParams (see https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout) */}
      <Suspense>
        <GoogleTagManagerScripts
          gtmId={config.gtmId}
          initialUserId={initialUserId}
          onLoadCallback={() => setIsGtagLoaded(true)}
        />
        {isGtagLoaded && <CookieBanner />}
      </Suspense>
    </>
  );
};
